import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import axios from '../axios';
import NotFound from './NotFound';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OrderDetails from '../components/OrderDetails';
import ConfirmChanges from '../components/ConfirmChanges';
import ImageLightbox from '../components/ImageLightbox';

export default function TryAndBuyOrder() {
  const { t, i18n } = useTranslation();
  const {lang} = useParams(); 
  const [searchParams] = useSearchParams();
  const trackingNumber = searchParams.get('tracking_number');
  const token = searchParams.get('token');

  const [orderTrackingNumber, setOrderTrackingNumber] = useState('');
  const [step, setStep] = useState('details');
  const [products, setProducts] = useState([]);
  const [codAmount, setCodAmount] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [toast, setToast] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingAmounts, setIsUpdatingAmounts] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const isArabic = i18n.language === 'ar' || lang === 'ar';

  useEffect(() => {
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  useEffect(() => {
    if (!trackingNumber || !token) {
      setIsLoading(false);
      return;
    }

    axios
      .get(`users/vendor-agents/orders/${trackingNumber}/?token=${token}`)
      .then((res) => {
        setOrderTrackingNumber(res.data.tracking_number);
        const mappedProducts = (res.data.buy_and_try_product_in_order ?? []).map((item) => ({
          id: item.id,
          name: item.product_name,
          price: item.unit_price,
          image: item.product_image_url,
          orderedQty: item.requested_quantity,
          receivedQty: item.delivered_quantity,
          maxQty: item.requested_quantity,
          bundleId: item.bundle_id || null,
        }));
        setProducts(mappedProducts);
      })
      .catch((error) => {
        const firstError = Object.values(error.response?.data || {})[0];
        const errorMessage = Array.isArray(firstError)
          ? firstError[0]
          : firstError || 'Failed to load order details';
        setToast({ message: errorMessage, type: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [trackingNumber, token]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const getBundleBaseItem = (bundleId) => {
    const group = products.filter((p) => p.bundleId === bundleId);
    if (group.length === 0) return null;
    return group.reduce((min, p) => (p.maxQty < min.maxQty ? p : min), group[0]);
  };

  const isBundleControllable = (product) => {
    if (!product.bundleId) return true;
    const baseItem = getBundleBaseItem(product.bundleId);
    return baseItem ? product.id === baseItem.id : true;
  };

  const groupProductsByBundle = (list) => {
    const groups = [];
    const seenBundles = new Map();
    list.forEach((p) => {
      if (!p.bundleId) {
        groups.push([p]);
        return;
      }
      if (seenBundles.has(p.bundleId)) {
        groups[seenBundles.get(p.bundleId)].push(p);
      } else {
        seenBundles.set(p.bundleId, groups.length);
        groups.push([p]);
      }
    });
    return groups;
  };

  const handleQtyChange = (id, delta) => {
    setProducts((prev) => {
      const changed = prev.find((p) => p.id === id);
      if (!changed) return prev;

      if (!changed.bundleId) {
        return prev.map((p) =>
          p.id === id
            ? { ...p, receivedQty: Math.max(0, Math.min(p.maxQty, p.receivedQty + delta)) }
            : p
        );
      }

      const bundleGroup = prev.filter((p) => p.bundleId === changed.bundleId);
      const baseItem = bundleGroup.reduce(
        (min, p) => (p.maxQty < min.maxQty ? p : min),
        bundleGroup[0]
      );

      if (changed.id !== baseItem.id) return prev;

      return prev.map((p) => {
        if (p.bundleId !== changed.bundleId) return p;
        const ratio = p.maxQty / baseItem.maxQty;
        const itemDelta = delta * ratio;
        return {
          ...p,
          receivedQty: Math.max(0, Math.min(p.maxQty, p.receivedQty + itemDelta)),
        };
      });
    });
  };

  const handleUpdateAmounts = () => {
    const items = products.map((p) => ({
      id: p.id,
      delivered_quantity: p.receivedQty,
    }));
    setIsUpdatingAmounts(true);
    axios
      .patch(
        `users/vendor-agents/orders/${trackingNumber}/buy-and-try-products/?token=${token}&dry_run=true`,
        { items }
      )
      .then((res) => {
        setCodAmount(res.data.cash_amount);
      })
      .catch((error) => {
        setToast({ message: t('Failed to calculate amount'), type: 'error' });
      })
      .finally(() => {
        setIsUpdatingAmounts(false);
      });
  };

  const handleConfirm = () => {
    const items = products.map((p) => ({
      id: p.id,
      delivered_quantity: p.receivedQty,
    }));
    setIsConfirming(true);
    axios
      .patch(
        `users/vendor-agents/orders/${trackingNumber}/buy-and-try-products/?token=${token}&dry_run=false`,
        { items }
      )
      .then((res) => {
        setToast({ message: t('Changes updated successfully'), type: 'success' });
        setCodAmount(null);
        setStep('details');

      })
      .catch((error) => {
        setToast({ message: t('Failed to update changes'), type: 'error' });
      })
      .finally(() => {
        setIsConfirming(false);
      });
  };

  const handleCancel = () => {
    setCodAmount(null);
    setStep('details');
  };

  if (!trackingNumber || !token) {
    return <NotFound />;
  }

  return (
    <main className={`${isArabic ? 'rtl' : 'ltr'} min-h-dvh bg-white sm:px-5 sm:py-3 text-darkblue`}>
      {toast && (
        <div className="fixed top-4 inset-x-0 z-[100] flex justify-center px-4">
          <div
            className={`text-white text-sm px-4 py-2 rounded-xl shadow-lg flex items-center gap-2 ${
              toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
            }`}
          >
            <span className="text-lg">{toast.type === 'error' ? <WarningAmberIcon fontSize="small" /> : <CheckIcon fontSize="small" />}</span>
            <span>{t(toast.message)}</span>
          </div>
        </div>
      )}

      <section className="relative mx-auto flex sm:min-h-[calc(100dvh-24px)] min-h-dvh sm:max-w-sm w-full flex-col overflow-hidden sm:rounded-[34px] sm:border-[3px] border-coconut-white bg-white shadow-[0_22px_55px_rgba(10,50,102,0.08)]">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="w-10 h-10 border-4 border-coconut-white border-t-bright-blue rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="relative z-10 flex flex-1 flex-col px-6 pb-28 pt-14">
            <img
              className="mx-auto mb-8 h-auto w-[162px]"
              src="https://production-publicstorage1123538a-1j4jxuuq2c9nf.s3.eu-west-3.amazonaws.com/customer-portal/logo.png"
              alt="ShipBlu Logo"
            />

            {step === 'details' && (
              <OrderDetails
                trackingNumber={orderTrackingNumber}
                products={products}
                codAmount={codAmount}
                onQtyChange={handleQtyChange}
                onUpdateAmounts={handleUpdateAmounts}
                onProceed={() => setStep('confirm')}
                onImageClick={setPreviewImage}
                isBundleControllable={isBundleControllable}
                groupProductsByBundle={groupProductsByBundle}
                isUpdatingAmounts={isUpdatingAmounts}
              />
            )}

            {step === 'confirm' && (
              <ConfirmChanges
                codAmount={codAmount}
                products={products}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                isConfirming={isConfirming}
              />
            )}
          </div>
        )}

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 overflow-hidden">
          <div className="absolute -bottom-9 -left-10 h-24 sm:w-[230px] w-[253px] rounded-[50%] bg-calcareous-sinter opacity-90"></div>
          <div className="absolute -bottom-11 right-[-36px] h-32 sm:w-[260px] w-[286px] rotate-[-12deg] rounded-[50%] bg-icy-plains opacity-85"></div>
          <div className="absolute -bottom-10 left-[50px] h-24 sm:w-[250px] w-[275px] rotate-[10deg] rounded-[50%] bg-white opacity-70"></div>
        </div>

        {previewImage && (
          <ImageLightbox src={previewImage} onClose={() => setPreviewImage(null)} />
        )}
      </section>
    </main>
  );
}